import React from 'react';
import { PenLine } from 'lucide-react';

import { ucwords } from '../../../../lib/strings';
import dayjs from '../../../../lib/day';
import { EYEBROW, SIDE_CARD } from './SideCards';

const formatPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p || '-';
};

// Yes / No, or a dash when they never answered (QA 2026-10-13: empty read No).
const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : null);

const Field = ({ label, children }) => (
  <div className="min-w-0 py-3">
    <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</dt>
    <dd className="mb-0 mt-0.5 truncate text-sm font-semibold text-slate-900 dark:text-white">{children || '-'}</dd>
  </div>
);

const Group = ({ title, children }) => (
  <section className={SIDE_CARD}>
    <div className={EYEBROW}>{title}</div>
    <dl className="mb-0 mt-1 grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-2 sm:gap-x-8 sm:divide-y-0 dark:divide-white/[0.06]">
      {children}
    </dl>
  </section>
);

// Their details, grouped, with one Edit (the same editor as before).
const DetailsPanel = ({ claim, onEdit }) => {
  const u = claim.user || {};
  const dates = Array.isArray(u.userServiceDates) ? u.userServiceDates : [];
  const address = [u.addressLine1, u.addressLine2, [u.addressCity, u.addressState].filter(Boolean).join(', '), u.addressZip]
    .filter(Boolean)
    .join(' ');
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="mb-0 text-sm text-slate-500 dark:text-slate-400">
          Keep these up to date so your claim is filed correctly.
        </p>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:!bg-white dark:!text-slate-900"
        >
          <PenLine size={15} /> Edit my details
        </button>
      </div>
      <Group title="Contact">
        <Field label="Email">{u.userEmail}</Field>
        <Field label="Phone">{u.userPhone ? `${formatPhone(u.userPhone)}${u.userPhoneType ? ` (${ucwords(u.userPhoneType)})` : ''}` : null}</Field>
        <Field label="Mailing address">{address}</Field>
        <Field label="Date of birth">{u.userDateOfBirth ? dayjs.utc(u.userDateOfBirth).format('MMM D, YYYY') : null}</Field>
      </Group>
      <Group title="Family">
        <Field label="Married">{yesNo(u.userMarried)}</Field>
        <Field label="Children under 18">{u.userDependentsUnder18 ?? 0}</Field>
        <Field label="SSN">
          {u.userSsnLast4 ? `•••-••-${u.userSsnLast4}` : u.userSsn ? `•••-••-${String(u.userSsn).slice(-4)}` : null}
        </Field>
      </Group>
      <Group title="Service">
        <Field label="Military status">{u.userMilitaryStatus ? ucwords(u.userMilitaryStatus) : null}</Field>
        <Field label="Discharge">{u.userDischargeType ? ucwords(u.userDischargeType) : null}</Field>
        <Field label="Combat veteran">{yesNo(u.userCombatVeteran)}</Field>
        <Field label="Service dates">
          {dates.length ? dates.map((d) => `${d.start} to ${d.end}`).join(', ') : null}
        </Field>
      </Group>
      {claim.claimRatingsBreakdown?.length ? (
        <Group title="Your current ratings">
          {claim.claimRatingsBreakdown.map((r, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Field key={i} label={r.label}>{`${r.rating}%`}</Field>
          ))}
        </Group>
      ) : null}
    </div>
  );
};

export default DetailsPanel;
